import { Directive, ElementRef, HostBinding, HostListener, inject, input, OnInit, output, Renderer2, DOCUMENT } from '@angular/core';
import { FileTypeUtils } from '@app/modules/core/services/file-type-utils';

export interface FileUploadEvent {
  data: string | ArrayBuffer;
  file: File;
}

export enum ReadFileFormat {
  None = 0,
  ArrayBuffer = 1,
  DataUrl = 2,
  BinaryString = 3,
  Text = 4
}

@Directive({
  selector: '[topoFileUpload]'
})
export class FileUploadDirective implements OnInit {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);

  private utils = inject(FileTypeUtils);

  acceptedFileTypes = input(this.utils.allFileFormatsForUpload);
  maxFileSize = input(5242880); // 5MB in bytes
  readFileFormat = input(ReadFileFormat.None, {
    transform: (value: string | number = 0) => {
      const parsedValue = typeof value === 'string' ? parseInt(value) : value;
      return parsedValue as ReadFileFormat;
    }
  });
  inputZIndex = input(2, {
    transform: (value: string | number = 2) => (typeof value === 'string' ? (parseInt(value) ?? 2) : value)
  });

  fileUploaded = output<FileUploadEvent>();
  fileUploadError = output<Error>();

  ngOnInit(): void {
    const fileInput = this.document.createElement('input');

    fileInput.type = 'file';
    fileInput.accept = this.acceptedFileTypes();
    fileInput.style.position = 'absolute';
    fileInput.style.opacity = '0';
    fileInput.style.zIndex = this.inputZIndex().toString();
    fileInput.style.width = '100%';
    fileInput.style.height = '100%';
    fileInput.style.top = '0';
    fileInput.style.left = '0';
    fileInput.style.cursor = 'pointer';

    fileInput.onchange = function (event: InputEvent) {
      const files = (event.target as HTMLInputElement).files;
      this.handleFileUpload(files);
    }.bind(this);

    this.elementRef.nativeElement.style.position = 'relative';
    this.renderer.appendChild(this.elementRef.nativeElement, fileInput);
  }

  @HostBinding('class.file-dropzone') canDrop: boolean;

  @HostListener('dragover', ['$event'])
  @HostListener('dragleave', ['$event'])
  listenToDragEvents(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.canDrop = event.type === 'dragover';
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.canDrop = false;
    const files = event.dataTransfer.files;
    this.handleFileUpload(files);
  }

  private handleFileUpload(files: FileList): void {
    if (files?.length > 0) {
      if (!this.acceptedFileTypes()) {
        this.fileUploadError.emit(new Error('FILE-UPLOAD.ERROR.ACCEPTED-FILE-TYPES'));
        return;
      } else if (!this.readFileFormat()) {
        this.fileUploadError.emit(new Error('FILE-UPLOAD.ERROR.READ-FILE-FORMAT'));
        return;
      }

      const file = files[0];

      const validTypeRegex = new RegExp(this.acceptedFileTypes().replace(/\*/g, '.*').replace(/\,/g, '|'));
      const isValidFileType = validTypeRegex.test(file.type);

      if (!isValidFileType) {
        this.fileUploadError.emit(new Error('FILE-UPLOAD.ERROR.FILE-TYPE'));
        return;
      }

      if (file.size > this.maxFileSize()) {
        this.fileUploadError.emit(new Error('FILE-UPLOAD.ERROR.FILE-SIZE'));
        return;
      }

      const fileReader = new FileReader();

      fileReader.onloadend = (event: ProgressEvent<FileReader>) => {
        const data = event.target.result;
        this.fileUploaded.emit({ data, file });
      };

      switch (this.readFileFormat()) {
        case ReadFileFormat.ArrayBuffer:
          fileReader.readAsArrayBuffer(file);
          break;

        case ReadFileFormat.BinaryString:
          fileReader.readAsBinaryString(file);
          break;

        case ReadFileFormat.DataUrl:
          fileReader.readAsDataURL(file);
          break;

        case ReadFileFormat.Text:
          fileReader.readAsText(file);
          break;
        case ReadFileFormat.None:
          this.fileUploadError.emit(new Error('FILE-UPLOAD.ERROR.READ-FILE-TYPE-NONE'));
      }
    }
  }
}
